import React, { useState, useEffect, useCallback } from "react";

const LS_DISMISS = "econdo_pwa_install_hint_dismissed";

function isStandalone() {
  if (typeof window === "undefined") return true;
  return (
    window.matchMedia("(display-mode: standalone)").matches ||
    window.matchMedia("(display-mode: fullscreen)").matches ||
    window.navigator.standalone === true
  );
}

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(max-width: 768px)").matches;
}

/**
 * Banner opcional (PWA fase 1): convite a adicionar à tela inicial no mobile.
 * Não altera service worker nem cache — só UX.
 */
export default function PwaInstallHint() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (isStandalone()) return;
    try {
      if (localStorage.getItem(LS_DISMISS) === "1") return;
    } catch {
      /* ignore */
    }
    if (!isMobileViewport()) return;
    setVisible(true);
  }, []);

  useEffect(() => {
    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    return () => window.removeEventListener("beforeinstallprompt", onBeforeInstall);
  }, []);

  const dismiss = useCallback(() => {
    try {
      localStorage.setItem(LS_DISMISS, "1");
    } catch {
      /* ignore */
    }
    setVisible(false);
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt?.prompt) return;
    try {
      await deferredPrompt.prompt();
      await deferredPrompt.userChoice;
    } catch {
      /* ignore */
    }
    setDeferredPrompt(null);
  };

  if (!visible) return null;

  return (
    <div
      className="pcp-card"
      role="region"
      aria-label="Instalar aplicativo"
      style={{
        marginBottom: 14,
        padding: "12px 14px",
        border: "1px solid #BFDBFE",
        background: "linear-gradient(135deg, #EFF6FF 0%, #F8FAFC 100%)",
        borderRadius: 10,
      }}
    >
      <div style={{ display: "flex", alignItems: "flex-start", gap: 10 }}>
        <div
          style={{
            width: 40,
            height: 40,
            borderRadius: 10,
            background: "#1B3A6B",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <i className="bi bi-phone" style={{ fontSize: 20 }} aria-hidden />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, fontSize: 14, color:"var(--text-primary)", marginBottom: 4 }}>
            Abrir como aplicativo no celular
          </div>
          <div style={{ fontSize: 12.5, color:"var(--gray-500)", lineHeight: 1.45, marginBottom: 8 }}>
            É o mesmo portal do navegador: um atalho na tela inicial abre mais rápido e fica com
            aspecto de app.
          </div>
          <button
            type="button"
            onClick={() => setExpanded((e) => !e)}
            style={{
              background: "none",
              border: "none",
              color: "#1B3A6B",
              fontSize: 12,
              fontWeight: 600,
              cursor: "pointer",
              padding: 0,
              textDecoration: "underline",
            }}
          >
            {expanded ? "Ocultar instruções" : "Como instalar?"}
          </button>
          {expanded && (
            <ul
              style={{
                margin: "10px 0 0",
                paddingLeft: 18,
                fontSize: 12,
                color:"var(--gray-600)",
                lineHeight: 1.55,
              }}
            >
              <li>
                <strong>Android (Chrome):</strong> menu ⋮ → &quot;Instalar app&quot; ou
                &quot;Adicionar à página inicial&quot;.
              </li>
              <li>
                <strong>iPhone (Safari):</strong> botão compartilhar{" "}
                <span aria-hidden>□↑</span> → &quot;Adicionar à Tela de Início&quot;.
              </li>
            </ul>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 12 }}>
            {deferredPrompt && (
              <button
                type="button"
                onClick={handleInstall}
                style={{
                  padding: "8px 14px",
                  borderRadius: 8,
                  border: "none",
                  background: "#1B3A6B",
                  color: "#fff",
                  fontSize: 13,
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                <i className="bi bi-download me-1" aria-hidden />
                Instalar
              </button>
            )}
            <button
              type="button"
              onClick={dismiss}
              style={{
                padding: "8px 14px",
                borderRadius: 8,
                border: "1px solid #CBD5E1",
                background:"var(--bg-surface)",
                color:"var(--gray-500)",
                fontSize: 13,
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              Não mostrar de novo
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Fechar"
          style={{
            background: "none",
            border: "none",
            color:"var(--gray-400)",
            cursor: "pointer",
            fontSize: 18,
            lineHeight: 1,
            padding: 0,
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>
    </div>
  );
}
